import { useQuery } from '@tanstack/react-query';
import { SIZE_LABELS, sizeRangeLabel, type SizeThresholds } from '@rundown/domain';
import { EmptyState } from '../../../components';
import { workspaceSettingsQueryOptions } from '../api';
import { useUpdateWorkspaceSettings } from '../useSettingsMutations';
import { CommittedField, InlineStatus, SectionCard } from '../fields';

/**
 * Name, timezone, the three retainer numbers the rest of the product derives
 * from, and the day bands that turn an estimate into a size label. Every field
 * commits independently — see CommittedField — so a change to one number never
 * touches the others in the trail.
 */
export function WorkspaceSection({ workspaceId }: { workspaceId: string }) {
  const query = useQuery(workspaceSettingsQueryOptions(workspaceId));
  const update = useUpdateWorkspaceSettings(workspaceId);

  if (query.isPending) return <p className="fs-12 text-muted">Loading…</p>;
  if (query.error !== null) {
    return <EmptyState title="Could not load workspace settings" description={query.error.message} />;
  }

  const settings = query.data;
  const busy = update.isPending;

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Workspace" description="The identity of this client's portal.">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(11.25rem,1fr))] gap-3">
          <CommittedField
            label="Name"
            value={settings.name}
            disabled={busy}
            onCommit={(name) => name.trim() !== '' && update.mutate({ name: name.trim() })}
          />
          <CommittedField
            label="Delivery team"
            value={settings.vendorName}
            placeholder="Your agency's name"
            disabled={busy}
            onCommit={(vendorName) =>
              vendorName.trim() !== '' && update.mutate({ vendorName: vendorName.trim() })
            }
          />
          <CommittedField
            label="Timezone"
            value={settings.timezone}
            placeholder="Australia/Sydney"
            disabled={busy}
            onCommit={(timezone) => timezone.trim() !== '' && update.mutate({ timezone: timezone.trim() })}
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Retainer terms"
        description="What a dispute over billing turns on — every change here is written to the audit trail and shown to the client."
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(11.25rem,1fr))] gap-3">
          <CommittedField
            label="Monthly capacity (hours)"
            type="number"
            value={String(settings.monthlyCapacityHours)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 0) {
                update.mutate({ monthlyCapacityHours: Math.round(parsed) });
              }
            }}
          />
          <CommittedField
            label="Hotfix reserve (%)"
            type="number"
            value={String(settings.hotfixReservePct)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 100) {
                update.mutate({ hotfixReservePct: Math.round(parsed) });
              }
            }}
          />
          <CommittedField
            label="Included credit units"
            type="number"
            value={String(settings.includedCreditUnits)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 0) {
                update.mutate({ includedCreditUnits: Math.round(parsed) });
              }
            }}
          />
        </div>
        <InlineStatus pending={update.isPending} error={update.error?.message ?? null} />
      </SectionCard>

      <SectionCard
        title="Sizing"
        description="Small, Medium, Large and XL are labels for a request's estimated hours — nothing is billed or planned from them. These are where the lines fall."
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(11.25rem,1fr))] gap-3">
          <CommittedField
            label="Hours in a working day"
            type="number"
            value={String(settings.sizeHoursPerDay)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 24) {
                update.mutate({ sizeHoursPerDay: Math.round(parsed) });
              }
            }}
          />
          <CommittedField
            label="Small: up to (days)"
            type="number"
            value={String(settings.sizeSmallMaxDays)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 1) {
                update.mutate({ sizeSmallMaxDays: Math.round(parsed) });
              }
            }}
          />
          <CommittedField
            label="Medium: up to (days)"
            type="number"
            value={String(settings.sizeMediumMaxDays)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 1) {
                update.mutate({ sizeMediumMaxDays: Math.round(parsed) });
              }
            }}
          />
          <CommittedField
            label="Large: up to (days)"
            type="number"
            value={String(settings.sizeLargeMaxDays)}
            disabled={busy}
            onCommit={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed) && parsed >= 1) {
                update.mutate({ sizeLargeMaxDays: Math.round(parsed) });
              }
            }}
          />
        </div>
        <SizeBands thresholds={thresholdsOf(settings)} />
        <InlineStatus pending={update.isPending} error={update.error?.message ?? null} />
      </SectionCard>
    </div>
  );
}

/** The four flat columns, as the one object the rest of the app passes around. */
function thresholdsOf(settings: {
  readonly sizeHoursPerDay: number;
  readonly sizeSmallMaxDays: number;
  readonly sizeMediumMaxDays: number;
  readonly sizeLargeMaxDays: number;
}): SizeThresholds {
  return {
    hoursPerDay: settings.sizeHoursPerDay,
    smallMaxDays: settings.sizeSmallMaxDays,
    mediumMaxDays: settings.sizeMediumMaxDays,
    largeMaxDays: settings.sizeLargeMaxDays,
  };
}

/**
 * The four bands read back as sentences, in days and in hours.
 *
 * Four number inputs do not add up to a rule in anyone's head — the question
 * being answered is "so what counts as Large now?", and this answers it from
 * the same `sizeRangeLabel` the request screen prints, so the preview cannot
 * describe bands the product does not use.
 */
function SizeBands({ thresholds }: { thresholds: SizeThresholds }) {
  const hours = (days: number) => days * thresholds.hoursPerDay;
  const rows = [
    { size: 'small', hours: `up to ${hours(thresholds.smallMaxDays)} h` },
    {
      size: 'medium',
      hours: `${hours(thresholds.smallMaxDays)}–${hours(thresholds.mediumMaxDays)} h`,
    },
    {
      size: 'large',
      hours: `${hours(thresholds.mediumMaxDays)}–${hours(thresholds.largeMaxDays)} h`,
    },
    { size: 'xl', hours: `over ${hours(thresholds.largeMaxDays)} h` },
  ] as const;

  return (
    <dl className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2">
      {rows.map((row) => (
        <div key={row.size} className="rounded-[9px] border border-line px-3 py-2">
          <dt className="fs-11 font-bold text-ink">{SIZE_LABELS[row.size]}</dt>
          {/* Days are the rule; hours are what an estimate is actually typed
              in, so the band is useless to a reviewer without both. */}
          <dd className="fs-11 text-muted">{sizeRangeLabel(row.size, thresholds)}</dd>
          <dd className="fs-10.5 text-faint">{row.hours}</dd>
        </div>
      ))}
    </dl>
  );
}
